/* ==========================================================================
   QW-AB — PDP Review Carousel behaviour
   Native theme port of the winning Intelligems PDP variation.

   The markup is rendered server-side by snippets/qw-review-carousel.liquid,
   so this file only owns behaviour:
     - dot navigation + active-dot sync while scrolling
     - desktop click & drag (mouse pointers only — touch keeps the browser's
       native momentum scrolling, which beats anything we would write)

   Registered as <qw-review-carousel>, so it re-initialises by itself when
   product-info re-renders the buy box on a variant change.
   ========================================================================== */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 5; // px before a pointerdown counts as a drag
  var SNAP_RESTORE_DELAY = 450; // fallback when scrollend is unsupported

  class QwReviewCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-qw-rv-track]');
      if (!this.track) return;

      this.cards = Array.from(this.track.children);
      this.dots = Array.from(this.querySelectorAll('[data-qw-rv-dot]'));
      if (this.cards.length === 0) return;

      this.session = 0; // bumped on every pointerdown; invalidates stale snap restores
      this.isDown = false;
      this.dragging = false;
      this.suppressClick = false;
      this.startX = 0;
      this.startScroll = 0;
      this.pointerId = null;
      this.snapTimer = null;
      this.scrollEndHandler = null;
      this.scrollTicking = false;

      this.onWindowPointerUp = this.endDrag.bind(this);
      this.controller = new AbortController();

      this.bindDots();
      this.bindScrollSync();
      this.bindDrag();
      this.syncDots();
    }

    disconnectedCallback() {
      window.removeEventListener('pointerup', this.onWindowPointerUp);
      this.cancelSnapRestore();
      if (this.controller) this.controller.abort();
    }

    /* ---------------- geometry ---------------- */

    get activeIndex() {
      var scroll = this.track.scrollLeft;
      var best = 0;
      var bestDistance = Infinity;

      for (var i = 0; i < this.cards.length; i++) {
        var distance = Math.abs(this.cards[i].offsetLeft - scroll);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }

      return best;
    }

    scrollToIndex(index) {
      var card = this.cards[index];
      if (!card) return;

      this.track.scrollTo({
        left: card.offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    /* ---------------- dots ---------------- */

    bindDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToIndex(index), { signal: this.controller.signal });
      });
    }

    syncDots() {
      var index = this.activeIndex;
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    bindScrollSync() {
      this.track.addEventListener(
        'scroll',
        () => {
          if (this.scrollTicking) return;
          this.scrollTicking = true;
          window.requestAnimationFrame(() => {
            this.scrollTicking = false;
            this.syncDots();
          });
        },
        { passive: true, signal: this.controller.signal }
      );
    }

    /* ---------------- scroll snap ----------------
       Snap is switched off for the duration of a drag, otherwise the snap
       engine fights every scrollLeft write. A restore from an earlier gesture
       must never fire during a later one, hence the session counter.
    ---------------------------------------------- */

    cancelSnapRestore() {
      window.clearTimeout(this.snapTimer);
      this.snapTimer = null;
      if (this.scrollEndHandler) {
        this.track.removeEventListener('scrollend', this.scrollEndHandler);
        this.scrollEndHandler = null;
      }
    }

    disableSnap() {
      this.cancelSnapRestore();
      this.track.style.scrollSnapType = 'none';
    }

    scheduleSnapRestore() {
      var mySession = this.session;
      var restore = () => {
        if (mySession !== this.session) return;
        this.cancelSnapRestore();
        this.track.style.scrollSnapType = '';
      };

      this.cancelSnapRestore();

      if ('onscrollend' in window) {
        this.scrollEndHandler = restore;
        this.track.addEventListener('scrollend', this.scrollEndHandler);
      }
      this.snapTimer = window.setTimeout(restore, SNAP_RESTORE_DELAY);
    }

    settle() {
      this.scrollToIndex(this.activeIndex);
      this.scheduleSnapRestore();
    }

    /* ---------------- desktop click & drag ---------------- */

    bindDrag() {
      var signal = this.controller.signal;

      this.track.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          this.session++;
          this.isDown = true;
          this.dragging = false;
          this.pointerId = event.pointerId;
          this.startX = event.clientX;
          this.startScroll = this.track.scrollLeft;

          // Off from the first frame, not from the drag threshold — an in-flight
          // smooth scroll or a pending restore would otherwise resist the gesture.
          this.disableSnap();
        },
        { signal }
      );

      this.track.addEventListener(
        'pointermove',
        (event) => {
          if (!this.isDown || event.pointerId !== this.pointerId) return;

          var delta = event.clientX - this.startX;

          if (!this.dragging) {
            if (Math.abs(delta) < DRAG_THRESHOLD) return;
            this.dragging = true;
            try {
              this.track.setPointerCapture(this.pointerId);
            } catch (e) {
              /* capture is best-effort */
            }
            this.track.classList.add('is-dragging');
          }

          event.preventDefault();

          var max = this.track.scrollWidth - this.track.clientWidth;
          this.track.scrollLeft = Math.max(0, Math.min(max, this.startScroll - delta));
        },
        { passive: false, signal }
      );

      this.track.addEventListener('pointerup', this.onWindowPointerUp, { signal });
      this.track.addEventListener('pointercancel', this.onWindowPointerUp, { signal });
      window.addEventListener('pointerup', this.onWindowPointerUp);

      this.track.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, signal }
      );

      // Native image/text dragging would hijack the gesture.
      this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    }

    endDrag(event) {
      if (!this.isDown) return;
      if (event && this.pointerId !== null && event.pointerId !== this.pointerId) return;

      this.isDown = false;

      if (this.pointerId !== null && this.track.hasPointerCapture && this.track.hasPointerCapture(this.pointerId)) {
        this.track.releasePointerCapture(this.pointerId);
      }
      this.pointerId = null;

      if (!this.dragging) {
        // Plain click: nothing moved, so put snap back immediately.
        this.track.style.scrollSnapType = '';
        return;
      }

      this.dragging = false;
      this.track.classList.remove('is-dragging');

      // Stop the drag-release from firing a click on whatever is under the cursor.
      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      this.settle();
    }
  }

  if (!customElements.get('qw-review-carousel')) {
    customElements.define('qw-review-carousel', QwReviewCarousel);
  }
})();
